import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Modal from 'modules/views/shared/Modal';
import { ReactComponent as ExclamationRound } from 'assets/images/ExclamationRound.svg';

import { StepKey, WIZARD_STEPS, getStepIndexFromPath, getStepTitle } from 'modules/views/shared/Wizard/config';
import { prettifyErrorResponse } from 'utils';
import { ROUTES } from 'constants/routes';
import { IOnboardingState } from 'modules/state-mgmt/onboarding/state';
import { useStylesProfileWarningModal } from '../styles';

interface IProfileWarningModalProps {
  worker: IOnboardingState;
  onEditWorker: (worker: IOnboardingState) => void;
  onClose: () => void;
  isVisible: boolean;
}

const ProfileWarningModal = ({ worker, onEditWorker, onClose, isVisible }: IProfileWarningModalProps) => {
  const { t } = useTranslation();
  const classes = useStylesProfileWarningModal();
  const navigate = useNavigate();

  const { message, route } = useMemo(() => {
    const { uploadingDetails } = worker;

    if (uploadingDetails?.errors?.pageUrl && uploadingDetails?.errors?.errorObject) {
      return {
        message: prettifyErrorResponse(uploadingDetails?.errors?.errorObject, true, '\n'),
        route: uploadingDetails?.errors?.pageUrl,
      };
    }

    let errorRoute = null;
    const sectionsCompleted = uploadingDetails?.sectionsCompleted || {};
    for (const step of WIZARD_STEPS) {
      if (!sectionsCompleted[step.key as StepKey]) {
        errorRoute = step.route;
        break; // Found the first incomplete step
      }
    }

    if (!errorRoute) {
      return {
        message: <>{t('workerStatus.profileWarningModal.pleaseUpload')}</>,
        route: ROUTES.CONSENT_FORM.path,
      };
    }

    const stepIndex = getStepIndexFromPath(errorRoute);
    const step = WIZARD_STEPS[stepIndex];
    const stepName = getStepTitle(step, undefined, t);
    return {
      message: (
        <>
          {t('workerStatus.profileWarningModal.pleaseComplete')} <span className={classes.stepName}>{stepName}</span>
        </>
      ),
      route: errorRoute,
    };
  }, [worker, t]);

  const onUpdateClick = useCallback(() => {
    onEditWorker(worker);
    onClose();
  }, [route, navigate, onClose, onEditWorker, worker]);

  if (!isVisible) {
    return null;
  }

  return (
    <Modal
      isVisible={isVisible}
      disableScrollLock={true}
      modalStyleClass={classes.modal}
      modalTitleStyleClass={classes.modalTitle}
      modalContentStyleClass={classes.modalContent}
      modalActionsStyleClass={classes.modalActions}
      testId="worker-profile-warning-modal"
      Content={
        <>
          <div className={classes.imageParent}>
            <ExclamationRound />
          </div>
          <p className={classes.title}>{t('workerStatus.profileWarningModal.title')}</p>
          <p className={classes.warningText} data-testid="worker-profile-warning-message">
            {message}
          </p>
        </>
      }
      actionButtons={[
        {
          label: t('workerStatus.profileWarningModal.buttonText'),
          styleClass: classes.button,
          testId: 'worker-profile-warning-update-button',
          onClick: onUpdateClick,
        },
      ]}
      onClose={onClose}
    />
  );
};

export default React.memo(ProfileWarningModal);
